import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Card, Progress, Table, Typography } from 'antd';

import {
  fetchAssessmentRisk,
  selectors,
  AssessmentPortfolioCheck,
  AssessmentRiskInstrument,
} from 'state';
import { formatters } from 'services';
import { LoadingScreen } from 'components';

import { PORTFOLIO_CHECK_LABELS } from './constants';

const utilizationColor = (utilization: number) =>
  utilization > 1 ? '#ff4d4f' : utilization > 0.8 ? '#faad14' : '#52c41a';

const UtilizationBar: React.FC<{ utilization: number }> = ({ utilization }) => (
  <div className="assessment-util-bar">
    <Progress
      percent={Math.min(100, utilization * 100)}
      strokeColor={utilizationColor(utilization)}
      format={() => formatters.percent0Digits(utilization)}
    />
  </div>
);

/** Live limit-utilization panel (GET risk/). Soft limits are visual only —
 *  breaches warn, they never block trading. Refreshed by the parent's socket
 *  ticks (elapsed_days) and whenever the player's holdings change. */
const RiskPanel: React.FC = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const risk = useSelector(selectors.assessmentRisk);
  // Object identity changes whenever holdings change (immer), so this is a
  // cheap "my trades just landed" refresh trigger.
  const holdings = useSelector(selectors.financialInstruments);

  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchAssessmentRisk({}));
  }, [dispatch, session?.id, session?.elapsed_days, holdings]);

  const hasDv01 = useMemo(
    () => (risk.item?.instruments || []).some(i => i.dv01_limit !== undefined),
    [risk.item],
  );

  const instrumentColumns = useMemo(() => {
    const base: any[] = [
      { title: 'Instrument', dataIndex: 'code', key: 'code', width: 110 },
      {
        title: 'Position',
        key: 'position',
        render: (_: unknown, record: AssessmentRiskInstrument) =>
          `${formatters.commas2Digits(record.position)} ${record.unit}`,
      },
      {
        title: 'Notional vs soft limit',
        key: 'utilization',
        width: 180,
        render: (_: unknown, record: AssessmentRiskInstrument) => (
          <UtilizationBar utilization={record.utilization} />
        ),
      },
    ];
    if (hasDv01) {
      base.push({
        title: 'DV01 vs soft limit',
        key: 'dv01',
        width: 180,
        render: (_: unknown, record: AssessmentRiskInstrument) =>
          record.dv01_limit !== undefined ? (
            <UtilizationBar utilization={record.dv01_utilization || 0} />
          ) : (
            '-'
          ),
      });
    }
    return base;
  }, [hasDv01]);

  const checkColumns = useMemo(
    () => [
      {
        title: 'Portfolio check',
        dataIndex: 'name',
        key: 'name',
        render: (name: string) => PORTFOLIO_CHECK_LABELS[name] || name,
      },
      {
        title: 'Value',
        dataIndex: 'value',
        key: 'value',
        align: 'right' as const,
        render: (value: number) => formatters.commas2Digits(value),
      },
      {
        title: 'Limit',
        dataIndex: 'limit',
        key: 'limit',
        align: 'right' as const,
        render: (limit: number) => formatters.commas2Digits(limit),
      },
      {
        title: 'Utilization',
        key: 'utilization',
        width: 180,
        render: (_: unknown, record: AssessmentPortfolioCheck) => (
          <UtilizationBar utilization={record.utilization} />
        ),
      },
    ],
    [],
  );

  if (!risk.fetched) return <LoadingScreen />;
  if (!risk.item) return null;
  if (risk.item.capture_only === true)
    return (
      <Typography.Paragraph type="secondary">
        Your responses are saved. Results pending review.
      </Typography.Paragraph>
    );

  const { instruments, portfolio, drawdown } = risk.item;
  const breachedCount =
    instruments.filter(i => i.breached || i.dv01_breached).length +
    portfolio.filter(c => c.breached).length;
  const drawdownUtilization = drawdown.stop_m
    ? drawdown.drawdown_m / drawdown.stop_m
    : 0;

  return (
    <div className="assessment-root">
      {breachedCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${breachedCount} soft limit${
            breachedCount > 1 ? 's' : ''
          } breached — soft limits never block trading, but breaches are scored.`}
        />
      )}
      {drawdown.stopped && (
        <Alert
          type="error"
          showIcon
          message="Drawdown stop reached (20% of starting capital)."
        />
      )}
      <div className="assessment-risk-grid">
        <div className="assessment-risk-col">
          <Typography.Title level={5}>Instrument limits</Typography.Title>
          <Table
            dataSource={instruments}
            columns={instrumentColumns}
            rowKey="code"
            size="small"
            bordered
            pagination={false}
            loading={risk.fetching}
            rowClassName={record =>
              record.breached || record.dv01_breached
                ? 'assessment-row-breached'
                : ''
            }
          />
        </div>
        <div className="assessment-risk-col">
          <Typography.Title level={5}>Portfolio checks</Typography.Title>
          <Table
            dataSource={portfolio}
            columns={checkColumns}
            rowKey="name"
            size="small"
            bordered
            pagination={false}
            loading={risk.fetching}
            rowClassName={record =>
              record.breached ? 'assessment-row-breached' : ''
            }
          />
          <Card size="small" title="Drawdown" style={{ marginTop: 16 }}>
            <div className="assessment-drawdown-figures">
              <span>
                NAV:{' '}
                <strong>{formatters.commas2Digits(drawdown.nav_m)}m</strong>
              </span>
              <span>
                Drawdown:{' '}
                <strong>
                  {formatters.commas2Digits(drawdown.drawdown_m)}m
                </strong>
              </span>
              <span>
                Stop:{' '}
                <strong>{formatters.commas2Digits(drawdown.stop_m)}m</strong>
              </span>
            </div>
            <div className="assessment-util-bar">
              <Progress
                percent={Math.min(100, drawdownUtilization * 100)}
                strokeColor={
                  drawdown.stopped
                    ? '#ff4d4f'
                    : utilizationColor(drawdownUtilization)
                }
                format={() => formatters.percent0Digits(drawdownUtilization)}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RiskPanel;
